// THE record History tab (BP-04, GUI View 1210). Every module's History tab
// renders this with config only: its `sourceType`, the record id, and
// optionally field labels and value formatting. None of them lays out,
// fetches, groups, sorts or formats a history row itself. The census test
// (src/history/__tests__/historyTabCensus.test.ts) fails a tab that does.
//
// Aaron 2026-09-30 (H244), on mustang flights/3455 › History: "history tab
// dosnt follow patterns check prod and check all history tabs". Before this
// file, 41 tabs hand-rolled some mix of prod's table ("<Module> Details"
// accordion; Date · Time · Action · Notes · User), Blank's accordion-by-day,
// hard-coded "dd-MMM-yyyy" dates, and getTimeFromISOString times. They all
// read the same route, and each read it a little differently.
//
// The layout is Blank's (src/blank/tabs/BlankHistory.tsx, which renders this
// component):
// - HistoryGridBox is the tab's ONE panel. The edit shell lists "history" in
//   TABS_WITH_OWN_PANEL, so there is no card in a card.
// - There is one accordion per tenant-local day, with an entry count. The
//   newest day is open.
// - Columns are Time · User · Action · Field · Old → New. Heading 16/10,
//   rows Roboto 14 #616161.
// - PAGE_SIZE is 5, with server pagination.
// What prod shows that Blank did not is supported: a status id read through
// the module's list (`formatValue`) and prod's field labels (the default).
// What Blank shows that prod does not is kept: the old value, now that the
// route serves it.
//
// The rules (tenant zone, order, one line per field) live in
// recordHistoryModel.ts, which is pure and tested.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Pagination, Typography } from "@mui/material";
import { useSnackbar } from "notistack";

import { CollapsibleSection } from "../components/CollapsibleSections";
import { HistoryGridBox } from "../components/DatagridProContnet";
import { useGlobalConfig } from "../hooks/useGlobalConfig";
import { tenantTimeZone } from "../utils/calendarDate";
import { formatTenantDate, tenantDatePattern } from "../utils/tenantDate";
import { fetchRecordHistoryPage } from "./historyApi";
import { HistoryValue } from "./HistoryValue";
import {
  buildHistoryDays,
  HISTORY_TAB_PAGE_SIZE,
  type HistoryLine,
  type HistoryModelOptions,
} from "./recordHistoryModel";
import type { HistoryRow } from "./types";

export interface RecordHistoryTabProps
  extends Pick<
    HistoryModelOptions,
    "fieldLabels" | "formatValue" | "ignoredFields"
  > {
  /** The history discriminator: the model's `__tablename__`, which the
   *  record_global_history_* listener writes (e.g. "flights_info"). It is
   *  NOT always the URL slug. See PATTERNS.md "Asymmetric source keys". */
  sourceType: string;
  /** The record id. Nothing is read until it is set (a new record). */
  sourceId: number | string | null | undefined;
  /** The record's noun: "Error fetching <noun> history", and "History starts
   *  once the <noun> is saved." */
  noun?: string;
  /** Bump to re-read after a save. */
  reloadKey?: unknown;
  /** Only for a route other than /v6/users/history/. It must return the same
   *  row shape. */
  fetchPage?: (
    page: number,
    size: number
  ) => Promise<{ rows: HistoryRow[]; total: number }>;
  /** For a History tab that stacks several sections in ONE HistoryGridBox
   *  (Jobs: record changes, demand, tickets; Items: origin, quotes, record).
   *  The panel and its 30px top margin are dropped, and the section gets
   *  `title` as its heading. */
  embedded?: boolean;
  /** The embedded section's heading. */
  title?: string;
}

export const RECORD_HISTORY_GRID = "120px 1fr 1fr 1fr 1fr";

const headSx = {
  display: "grid",
  gridTemplateColumns: RECORD_HISTORY_GRID,
  gap: 2,
  pt: "16px",
  pb: "10px",
  px: 0,
  borderBottom: "1px solid",
  borderColor: "divider",
  fontWeight: 700,
  fontFamily: "Roboto",
  fontSize: 14,
  color: "#000",
} as const;

const rowSx = {
  display: "grid",
  gridTemplateColumns: RECORD_HISTORY_GRID,
  gap: 2,
  py: 1.25,
  px: 0,
  borderTop: "1px solid",
  borderColor: "#f0f1f2",
  fontFamily: "Roboto",
  fontSize: 14,
  color: "#616161",
  overflowWrap: "anywhere",
} as const;

/** A value longer than this is cut, and its full text is the tooltip. A
 *  notes field would otherwise stretch one line across the page (the Quote
 *  tab's rule, src/quotes/QuoteHistory.tsx before H244). */
export const HISTORY_VALUE_MAX = 120;

function Change({ line, isCreate }: { line: HistoryLine; isCreate: boolean }) {
  if (isCreate) return <em>record created</em>;
  if (!line.field) return <>—</>;
  return (
    <>
      <HistoryValue value={line.oldValue} maxLength={HISTORY_VALUE_MAX} testId="record-history-old" />
      {" → "}
      <HistoryValue value={line.newValue} maxLength={HISTORY_VALUE_MAX} testId="record-history-new" changed />
    </>
  );
}

export default function RecordHistoryTab({
  sourceType,
  sourceId,
  noun = "record",
  reloadKey,
  fetchPage,
  fieldLabels,
  formatValue,
  ignoredFields,
  embedded = false,
  title = "Record changes",
}: RecordHistoryTabProps) {
  const { enqueueSnackbar } = useSnackbar();
  const globalConfig = useGlobalConfig();
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  // A tab says "no activity" only after a read that SUCCEEDED.
  const [state, setState] = useState<"loading" | "failed" | "ok">("loading");

  const read = useCallback(
    (p: number) =>
      fetchPage
        ? fetchPage(p, HISTORY_TAB_PAGE_SIZE)
        : fetchRecordHistoryPage(
            sourceType,
            sourceId as number | string,
            p,
            HISTORY_TAB_PAGE_SIZE
          ),
    [fetchPage, sourceType, sourceId]
  );

  useEffect(() => {
    if (sourceId == null || sourceId === "") return;
    let live = true;
    setState("loading");
    read(page)
      .then(({ rows: got, total }) => {
        if (!live) return;
        setRows(got);
        setPages(Math.max(1, Math.ceil(total / HISTORY_TAB_PAGE_SIZE)));
        setState("ok");
      })
      .catch(() => {
        if (!live) return;
        setRows([]);
        setState("failed");
        enqueueSnackbar(`Error fetching ${noun} history`, { variant: "error" });
      });
    return () => {
      live = false;
    };
  }, [read, page, sourceId, reloadKey, noun, enqueueSnackbar]);

  // BP-83 rule 1: times, days and day labels wait for the tenant config, so
  // they are never drawn on the wrong clock first.
  const datePattern = tenantDatePattern(globalConfig);
  const timeZone = tenantTimeZone(globalConfig);
  const days = useMemo(
    () =>
      globalConfig
        ? buildHistoryDays(rows, {
            timeZone,
            fieldLabels,
            formatValue,
            ignoredFields,
          })
        : [],
    [globalConfig, rows, timeZone, fieldLabels, formatValue, ignoredFields]
  );

  const unsaved = sourceId == null || sourceId === "";

  const body = (
    <>
      <Box sx={headSx}>
        <span>Time</span>
        <span>User</span>
        <span>Action</span>
        <span>Field</span>
        <span>Old → New</span>
      </Box>

      {unsaved && (
        <Box sx={{ px: 0, py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            History starts once the {noun} is saved.
          </Typography>
        </Box>
      )}

      {!unsaved && state !== "loading" && rows.length === 0 && (
        <Box sx={{ px: 0, py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {state === "failed"
              ? "History unavailable — the read failed."
              : "No activity recorded yet."}
          </Typography>
        </Box>
      )}

      {days.map(({ day, entries }, idx) => (
        // The day accordion is the shared collapsible section (its look is
        // owned there, src/components/CollapsibleSections.tsx).
        <CollapsibleSection
          key={day || "undated"}
          title={day ? formatTenantDate(day, datePattern) : "—"}
          count={`(${entries.length} ${entries.length === 1 ? "entry" : "entries"})`}
          body="rows"
          defaultExpanded={idx === 0}
          titleTestId="record-history-day"
        >
            {entries.map((e) =>
              e.lines.map((line, i) => (
                <Box
                  key={`${e.row.id}-${i}`}
                  data-testid="record-history-row"
                  sx={rowSx}
                >
                  <span>{i === 0 ? e.time : ""}</span>
                  <span>{i === 0 ? e.user : ""}</span>
                  <span>{i === 0 ? e.action : ""}</span>
                  <span>{line.field ? line.label : "—"}</span>
                  <span>
                    <Change line={line} isCreate={e.isCreate} />
                  </span>
                </Box>
              ))
            )}
        </CollapsibleSection>
      ))}

      {pages > 1 && (
        <Pagination
          count={pages}
          page={page}
          onChange={(_, v) => setPage(v)}
          sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <Box data-testid="record-history" sx={{ pt: "16px" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#000" }}>
          {title}
        </Typography>
        {body}
      </Box>
    );
  }
  return (
    <Box sx={{ pt: "30px" }} data-testid="record-history">
      {/* T=30 / L/R=0 — the canonical history-tab margin. */}
      <HistoryGridBox>{body}</HistoryGridBox>
    </Box>
  );
}
